import React, {useMemo, useState} from 'react';
import {useTranslation} from 'next-i18next';
import {Checkbox, CheckboxGroup, VStack} from '@tempo/core';
import {getTranslatedFeature} from '@deezer/next/shared/utils/user';
import type {Feature} from '@deezer/next/services/types/features';
import type {UserForm} from '@deezer/next/features/Account/type';

type UserRightsProps = {
	data: Feature[];
	isDisabled?: boolean;
	showHiddenRights?: boolean;
	onChange?: (value: Partial<UserForm>, isValid?: boolean) => void;
};

const UserRights = ({
	data,
	isDisabled,
	showHiddenRights,
	onChange,
}: UserRightsProps) => {
	const {t} = useTranslation('d4c');
	const [features, setFeatures] = useState(
		showHiddenRights ? data : data.filter(f => !f.isHidden),
	);
	const enabledFeatures = useMemo(
		() => features.filter(f => f.enabled),
		[features],
	);
	const items = useMemo(() => {
		return isDisabled ? enabledFeatures : features;
	}, [isDisabled, enabledFeatures, features]);

	const handleChange = (selectedLabels: string[]) => {
		const newFeatures = [...features].map(f => {
			return {
				...f,
				enabled: selectedLabels.includes(f.label),
			};
		});
		setFeatures(newFeatures);
		if (onChange) {
			onChange({features: newFeatures});
		}
	};

	if (!items.length) {
		return null;
	}

	return (
		<CheckboxGroup
			value={enabledFeatures.map(f => f.label)}
			onChange={handleChange}
			isDisabled={isDisabled}
		>
			<VStack alignItems="initial" spacing="spacing.s">
				{items.map(({label, isHidden}, index) => (
					<Checkbox
						data-testid={`user-manager-rights-${index}`}
						key={label}
						value={label}
						isDisabled={isDisabled || isHidden}
					>
						{getTranslatedFeature(label, t)}
					</Checkbox>
				))}
			</VStack>
		</CheckboxGroup>
	);
};

UserRights.defaultProps = {
	data: [],
};

export default UserRights;
